import { useState, useCallback, useEffect } from 'react'
import type { UploadService, ValidationResult, ImageFile, UploadProgress } from '../contracts/uploadService'
import { AppIcons } from './icons'

interface ImageUploadProps {
  uploadService: UploadService
  onUploadComplete: (images: ImageFile[]) => void
  onUploadError: (error: Error) => void
}

export function ImageUpload({ uploadService, onUploadComplete, onUploadError }: ImageUploadProps) {
  const [isDragOver, setIsDragOver] = useState(false)
  const [isProcessing, setIsProcessing] = useState(false)
  const [validationResults, setValidationResults] = useState<ValidationResult[]>([])
  const [uploadProgress, setUploadProgress] = useState<UploadProgress | null>(null)
  const [currentBatchId, setCurrentBatchId] = useState<string | null>(null)

  const handleFiles = useCallback(async (files: FileList) => {
    try {
      setIsProcessing(true)
      setValidationResults([])

      // Validate files first
      const results = uploadService.validateFiles(files)
      setValidationResults(results)

      // Check if any files are valid
      const validFiles = results.filter(r => r.isValid)
      if (validFiles.length === 0) {
        return // Don't process if no valid files
      }

      // Create FileList with only valid files
      const validFileList = {
        length: validFiles.length,
        item: (index: number) => validFiles[index]?.file || null,
        *[Symbol.iterator]() {
          for (let i = 0; i < this.length; i++) {
            const file = this.item(i)
            if (file) yield file
          }
        },
      } as FileList

      // Process the valid files with progress tracking
      const processedImages = await uploadService.processFiles(validFileList, (progress) => {
        setUploadProgress(progress)
        setCurrentBatchId(progress.batchId)
      })
      onUploadComplete(processedImages)

    } catch (error) {
      onUploadError(error instanceof Error ? error : new Error('Upload failed'))
    } finally {
      setIsProcessing(false)
      setUploadProgress(null)
      setCurrentBatchId(null)
    }
  }, [uploadService, onUploadComplete, onUploadError])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)

    if (e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files)
    }
  }, [handleFiles])

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFiles(e.target.files)
    }
  }, [handleFiles])

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)
  }, [])

  const supportedTypes = uploadService.getSupportedTypes()
  const maxFileSize = uploadService.getMaxFileSize()
  const maxBatchSize = uploadService.getMaxBatchSize()

  return (
    <div className="w-full">
      <div
        className={`
          border-2 border-dashed rounded-xl p-8 text-center transition-colors
          ${isDragOver ? 'border-blue-500 bg-blue-50' : 'border-gray-300'}
          ${isProcessing ? 'opacity-50 pointer-events-none' : 'hover:border-gray-400'}
        `}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
      >
        <div className="flex flex-col items-center space-y-4">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
            <svg
              className="w-8 h-8 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
              />
            </svg>
          </div>

          <div>
            <p className="text-lg font-medium text-gray-900">
              {isProcessing ? (
                uploadProgress ? (
                  `Processing ${uploadProgress.currentFile || 'images'}...`
                ) : (
                  'Processing images...'
                )
              ) : (
                'Drop images or videos here or click to browse'
              )}
            </p>
            {isProcessing && uploadProgress && (
              <div className="mt-2">
                {/* Progress bar */}
                <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                  <div 
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300" 
                    style={{ width: `${(uploadProgress.processedFiles / uploadProgress.totalFiles) * 100}%` }}
                  ></div>
                </div>
                
                {/* Detailed status */}
                <div className="flex items-center justify-between text-sm text-gray-600">
                  <span>
                    {uploadProgress.processedFiles} of {uploadProgress.totalFiles} files
                  </span>
                  {uploadProgress.currentFileStatus && (
                    <span className="flex items-center">
                      {uploadProgress.currentFileStatus === 'compressing' && (
                        <>
                          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Compressing large image...
                        </>
                      )}
                      {uploadProgress.currentFileStatus === 'generating-preview' && (
                        <>
                          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Generating preview...
                        </>
                      )}
                      {uploadProgress.currentFileStatus === 'uploading' && (
                        <>
                          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Processing...
                        </>
                      )}
                    </span>
                  )}
                </div>
              </div>
            )}
            {!isProcessing && (
              <div>
                <p className="text-sm text-gray-500 mt-2">
                  Images: {supportedTypes.filter(t => t.startsWith('image/')).map(type => type.split('/')[1]).join(', ')}
                  <br />
                  Videos: {supportedTypes.filter(t => t.startsWith('video/')).map(type => type.split('/')[1]).join(', ')}
                </p>
                <p className="text-sm text-gray-500">
                  Max file size: {Math.round(maxFileSize / 1024 / 1024)}MB • Max files: {maxBatchSize}
                </p>
              </div>
            )}
          </div>

          <label className="cursor-pointer">
            <input
              type="file"
              multiple
              accept={supportedTypes.join(',')}
              onChange={handleFileSelect}
              disabled={isProcessing}
              className="hidden"
            />
            <span className="bg-blue-500 text-white px-6 py-3 rounded-lg hover:bg-blue-600 transition-colors inline-block font-medium">
              {isProcessing ? 'Processing...' : 'Choose Files'}
            </span>
          </label>
        </div>
      </div>

      {/* Validation Results - Only show failed validations */}
      {validationResults.filter(r => !r.isValid).length > 0 && (
        <div className="mt-6 space-y-2">
          <h3 className="text-lg font-medium text-gray-900">File Validation Issues</h3>
          {validationResults.filter(r => !r.isValid).map((result, index) => (
            <div
              key={index}
              className={`p-4 rounded-xl border ${
                result.isValid ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
              }`}
            >
              <div className="flex items-center space-x-2">
                <span className={`w-2 h-2 rounded-full ${
                  result.isValid ? 'bg-green-500' : 'bg-red-500'
                }`} />
                <span className="font-medium">{result.file.name}</span>
                <span className="text-sm text-gray-500">
                  ({Math.round(result.file.size / 1024)} KB)
                </span>
              </div>

              {result.errors.length > 0 && (
                <div className="mt-2 ml-4">
                  {result.errors.map((error, errorIndex) => (
                    <p key={errorIndex} className="text-sm text-red-600 flex items-center">
                      <AppIcons.X className="w-4 h-4 mr-2 text-red-500" />
                      {error.message}
                    </p>
                  ))}
                </div>
              )}

              {result.warnings.length > 0 && (
                <div className="mt-2 ml-4">
                  {result.warnings.map((warning, warningIndex) => (
                    <p key={warningIndex} className="text-sm text-yellow-600 flex items-center">
                      <AppIcons.AlertTriangle className="w-4 h-4 mr-2 text-yellow-500" />
                      {warning.message}
                    </p>
                  ))}
                </div>
              )}

              {/* File size info for large images */}
              {result.file.size > 5 * 1024 * 1024 && (
                <div className="mt-2 ml-4">
                  <p className="text-sm text-blue-600 flex items-center">
                    <AppIcons.Info className="w-4 h-4 mr-2 text-blue-500" />
                    Large image will be automatically compressed for AI analysis (reduces processing time)
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
